/**
 * Suppliers Tab Component
 *
 * Tab content for managing suppliers in Settings page.
 * @see US-8.2 - Tab Fournisseurs dans Settings
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { Button, Input, Skeleton, Modal, ModalFooter, Pagination } from '@/components/ui';
import { useUrlParams } from '@/hooks';
import {
  useSuppliers,
  useSupplier,
  useDeactivateSupplier,
} from '../../../suppliers/hooks/useSuppliers';
import { SupplierCard } from '../../../suppliers/components/SupplierCard';
import { SupplierKPIs } from '../../../suppliers/components/SupplierKPIs';
import { SupplierFormModal } from '../../../suppliers/components/SupplierFormModal';
import { CatalogDrawer } from '../../../suppliers/components/catalog';
import type { SupplierWithStats } from '@bstock/shared';

export function SuppliersTab() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<SupplierWithStats | null>(null);
  const [deletingSupplier, setDeletingSupplier] = useState<SupplierWithStats | null>(null);

  // URL-based state for CatalogDrawer deep linking
  const { supplierId } = useParams<{ supplierId?: string }>();
  const navigate = useNavigate();

  // URL-synced params for pagination and search
  const { params, setParam, setParams } = useUrlParams({
    search: { default: '' },
    page: { default: 1, type: 'number' },
    limit: { default: 20, type: 'number' },
  });

  const deactivateMutation = useDeactivateSupplier();

  const { data: suppliersData, isLoading } = useSuppliers({
    search: params.search || undefined,
    isActive: true, // Only show active suppliers
    page: params.page,
    limit: params.limit,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const suppliers = suppliersData?.data ?? [];
  const pagination = suppliersData?.pagination;

  const handleCreate = () => {
    setEditingSupplier(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (supplier: SupplierWithStats) => {
    setEditingSupplier(supplier);
    setIsFormModalOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setParams({ search: value, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setParam('page', page);
  };

  const handleLimitChange = (limit: number) => {
    setParams({ limit, page: 1 });
  };

  const handleToggleStatus = (supplier: SupplierWithStats) => {
    setDeletingSupplier(supplier);
  };

  const handleConfirmDelete = async () => {
    if (deletingSupplier) {
      await deactivateMutation.mutateAsync(deletingSupplier.id);
      setDeletingSupplier(null);
    }
  };

  const handleViewCatalog = (supplier: SupplierWithStats) => {
    // Navigate to URL for deep linking support, preserving tab param
    navigate(`/parametres/suppliers/${supplier.id}/catalog?tab=suppliers`);
  };

  const handleCloseCatalog = () => {
    // Navigate back to suppliers tab
    navigate('/parametres?tab=suppliers');
  };

  // Fetch single supplier when coming from direct URL (may not be in list yet)
  const { data: supplierFromUrl } = useSupplier(supplierId ?? null);

  // Find the supplier from list first, fallback to direct fetch for deep linking
  const catalogSupplier = supplierId
    ? suppliers.find((s) => s.id === supplierId) ?? supplierFromUrl ?? null
    : null;

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditingSupplier(null);
  };

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <SupplierKPIs />

      {/* Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            type="text"
            value={params.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Rechercher un fournisseur..."
            className="pl-10"
          />
        </div>

        <Button variant="primary" onClick={handleCreate}>
          <Plus className="w-4 h-4" />
          Nouveau fournisseur
        </Button>
      </div>

      {/* Suppliers Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center">
          <p className="text-neutral-500">
            {params.search ? 'Aucun fournisseur trouve' : 'Aucun fournisseur cree'}
          </p>
          {!params.search && (
            <Button variant="primary" className="mt-4" onClick={handleCreate}>
              <Plus className="w-4 h-4" />
              Creer mon premier fournisseur
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={handleEdit}
              onToggleStatus={handleToggleStatus}
              onViewCatalog={handleViewCatalog}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          itemsPerPage={pagination.limit}
          totalItems={pagination.total}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
        />
      )}

      {/* Create/Edit Modal */}
      <SupplierFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseModal}
        supplier={editingSupplier}
      />

      {/* Catalog Drawer - URL-driven via supplierId param */}
      <CatalogDrawer
        supplier={catalogSupplier}
        isOpen={!!supplierId}
        onClose={handleCloseCatalog}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingSupplier}
        onClose={() => setDeletingSupplier(null)}
        title="Supprimer le fournisseur"
        size="sm"
      >
        <p className="text-sm text-neutral-600 mb-6">
          Etes-vous sur de vouloir supprimer "{deletingSupplier?.name}" ?
          <span className="block mt-2 text-neutral-500">
            Le fournisseur sera desactive et n'apparaitra plus dans la liste.
          </span>
        </p>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDeletingSupplier(null)}>
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            isLoading={deactivateMutation.isPending}
          >
            Supprimer
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
